import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Check, X, Trash2, CalendarDays, AlertTriangle, Building2 } from "lucide-react";
import TeamCalendar from "@/components/TeamCalendar";
import VacationProtocol from "@/components/VacationProtocol";

function parseISO(s) { return new Date(s + "T00:00:00"); }
function ymd(d) {
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function monthDays(d) {
  // Monday-first grid (6 rows x 7 cols = 42)
  const first = startOfMonth(d);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(first); start.setDate(first.getDate() - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const x = new Date(start); x.setDate(start.getDate() + i); return x;
  });
}
function sameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

export default function Vacation() {
  const { user } = useAuth();
  const [tab, setTab] = useState(user.role === "admin" ? "pending" : "mine");
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [pending, setPending] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [cal, setCal] = useState({ approved: [], own_pending: [] });
  const [stats, setStats] = useState(null);
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [open, setOpen] = useState(false);

  const reload = async () => {
    const tasks = [
      api.get("/vacations", { params: { scope: "mine" } }).then(({ data }) => setMine(data)),
      api.get("/vacations/calendar").then(({ data }) => setCal(data)),
      api.get(`/vacations/stats/${user.id}`).then(({ data }) => setStats(data)),
    ];
    if (user.role === "admin") {
      tasks.push(api.get("/vacations", { params: { scope: "pending" } }).then(({ data }) => setPending(data)));
      tasks.push(api.get("/vacations", { params: { scope: "all" } }).then(({ data }) => setAll(data)));
    }
    await Promise.all(tasks);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (user.role === "admin") api.get("/users").then(({ data }) => setEmployees(data)).catch(() => {});
  }, [user.role]);

  const decide = async (id, decision) => {
    const comment = decision === "rejected" ? (window.prompt("Begründung (optional):") || "") : "";
    try {
      await api.patch(`/vacations/${id}/decision`, { decision, comment });
      toast.success(decision === "approved" ? "Genehmigt" : "Abgelehnt");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Antrag wirklich zurückziehen?")) return;
    try {
      await api.delete(`/vacations/${id}`);
      toast.success("Zurückgezogen");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const adminDelete = async (v) => {
    const who = v.user_name || "Mitarbeiter";
    const range = `${v.start_date} → ${v.end_date}`;
    if (!window.confirm(`Urlaub von ${who} (${range}) wirklich löschen? Die Tage werden zurückgegeben.`)) return;
    try {
      await api.delete(`/vacations/${v.id}`);
      toast.success("Urlaub gelöscht");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="ph-label">Abwesenheit</p>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Urlaub</h1>
          <p className="text-slate-500 text-sm mt-1">Anträge stellen, Kalender prüfen und Genehmigungen verwalten.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-new-vacation" className="bg-kauz-navy hover:bg-kauz-navy-dark h-10">
              <Plus className="h-4 w-4 mr-2" /> {user.role === "admin" ? "Urlaub eintragen" : "Urlaubsantrag"}
            </Button>
          </DialogTrigger>
          <NewVacationDialog
            isAdmin={user.role === "admin"}
            employees={employees}
            currentUserId={user.id}
            onCreated={() => { reload(); setOpen(false); }}
          />
        </Dialog>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MiniStat label="Resturlaub" value={stats.remaining_days} unit="Tage" tone="navy" />
          <MiniStat label="Genommen" value={stats.taken_days} unit="Tage" tone="teal" />
          <MiniStat label="Insgesamt" value={stats.total_days} unit="Tage" tone="slate" />
          <MiniStat label="Offene Anträge" value={stats.pending_requests} unit="" tone="gold" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {user.role === "admin" && <TabsTrigger value="pending" data-testid="tab-pending">Zu prüfen ({pending.length})</TabsTrigger>}
          <TabsTrigger value="mine" data-testid="tab-mine">Meine Anträge</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">Kalender</TabsTrigger>
          <TabsTrigger value="protocol" data-testid="tab-protocol">Protokoll</TabsTrigger>
          {user.role === "admin" && <TabsTrigger value="all" data-testid="tab-all">Alle</TabsTrigger>}
          {user.role === "admin" && <TabsTrigger value="company" data-testid="tab-company">Betriebsferien</TabsTrigger>}
        </TabsList>

        {user.role === "admin" && (
          <TabsContent value="pending" className="mt-4">
            <RequestList items={pending} actions={(v) => (
              <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => decide(v.id, "approved")} data-testid={`approve-${v.id}`}>
                  <Check className="h-4 w-4 mr-1" /> Genehmigen
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-red-700 border-red-200 hover:bg-red-50" onClick={() => decide(v.id, "rejected")} data-testid={`reject-${v.id}`}>
                  <X className="h-4 w-4 mr-1" /> Ablehnen
                </Button>
              </>
            )} emptyText="Keine offenen Anträge." showUser />
          </TabsContent>
        )}

        <TabsContent value="mine" className="mt-4">
          <RequestList items={mine} actions={(v) => v.status === "pending" && (
            <Button size="sm" variant="outline" className="h-8 text-red-700 border-red-200 hover:bg-red-50" onClick={() => cancel(v.id)}>
              <Trash2 className="h-4 w-4 mr-1" /> Zurückziehen
            </Button>
          )} emptyText="Du hast noch keine Anträge gestellt." />
        </TabsContent>

        <TabsContent value="calendar" className="mt-4">
          <TeamCalendar />
        </TabsContent>

        <TabsContent value="protocol" className="mt-4">
          <VacationProtocol
            isAdmin={user.role === "admin"}
            employees={employees}
            currentUserId={user.id}
            currentUserName={user.name}
          />
        </TabsContent>

        {user.role === "admin" && (
          <TabsContent value="all" className="mt-4">
            <RequestList items={all} actions={(v) => (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-red-700 border-red-200 hover:bg-red-50"
                onClick={() => adminDelete(v)}
                data-testid={`admin-delete-${v.id}`}
              >
                <Trash2 className="h-4 w-4 mr-1" /> Löschen
              </Button>
            )} emptyText="Keine Anträge im System." showUser />
          </TabsContent>
        )}

        {user.role === "admin" && (
          <TabsContent value="company" className="mt-4">
            <CompanyVacationsPanel onChange={reload} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function MiniStat({ label, value, unit, tone }) {
  const tones = { sky: "text-sky-700", teal: "text-teal-700", slate: "text-slate-700", amber: "text-amber-700", orange: "text-kauz-navy", navy: "text-kauz-navy", gold: "text-kauz-gold-dark", rose: "text-rose-600" };
  const rings = { sky: "border-t-sky-300", teal: "border-t-teal-300", slate: "border-t-slate-300", amber: "border-t-amber-300", orange: "border-t-kauz-navy", navy: "border-t-kauz-navy", gold: "border-t-kauz-gold", rose: "border-t-rose-300" };
  return (
    <div className={`ph-card p-4 border-t-[3px] ${rings[tone] || "border-t-slate-200"}`}>
      <p className="ph-label">{label}</p>
      <div className="mt-2 flex items-end gap-1.5">
        <span className={`ph-kpi text-3xl ${tones[tone]}`}>{value}</span>
        {unit && <span className="text-xs text-slate-500 mb-1">{unit}</span>}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const m = {
    pending: ["bg-amber-50 text-amber-800 border-amber-200", "Offen"],
    approved: ["bg-emerald-50 text-emerald-800 border-emerald-200", "Genehmigt"],
    rejected: ["bg-red-50 text-red-800 border-red-200", "Abgelehnt"],
  };
  const [cls, label] = m[status] || m.pending;
  return <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md border ${cls}`}>{label}</span>;
}

function RequestList({ items, actions, emptyText, showUser }) {
  if (items.length === 0) return <div className="ph-card p-12 text-center text-slate-400 text-sm">{emptyText}</div>;
  return (
    <ul className="space-y-2">
      {items.map((v) => (
        <li key={v.id} data-testid={`vacation-row-${v.id}`} className="ph-card p-4 flex flex-col md:flex-row md:items-center md:gap-4 gap-2">
          <div className="h-10 w-10 rounded-md bg-slate-50 border border-slate-200 grid place-items-center md:flex-shrink-0">
            <CalendarDays className="h-5 w-5 text-slate-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {showUser && <span className="text-sm font-semibold text-slate-900">{v.user_name}</span>}
              <span className="text-sm text-slate-700">{v.start_date} → {v.end_date}</span>
              <span className="text-xs text-slate-400">· {v.days} Werktage</span>
              <StatusBadge status={v.status} />
            </div>
            {v.reason && <div className="text-xs text-slate-500 mt-1">„{v.reason}“</div>}
            {v.decision_comment && <div className="text-xs text-slate-500 mt-1">Kommentar: {v.decision_comment}</div>}
          </div>
          <div className="flex items-center gap-2">{actions(v)}</div>
        </li>
      ))}
    </ul>
  );
}

function NewVacationDialog({ onCreated, isAdmin, employees, currentUserId }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [forUserId, setForUserId] = useState("SELF");
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);

  // live conflict check
  useEffect(() => {
    if (!start || !end) { setConflict(null); return; }
    if (parseISO(end) < parseISO(start)) { setConflict(null); return; }
    let alive = true;
    api.get("/vacations/calendar").then(({ data }) => {
      if (!alive) return;
      const s = parseISO(start), e = parseISO(end);
      const c = data.approved.find((v) => {
        const vs = parseISO(v.start_date), ve = parseISO(v.end_date);
        return s <= ve && vs <= e;
      });
      setConflict(c || null);
    });
    return () => { alive = false; };
  }, [start, end]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { start_date: start, end_date: end, reason };
      if (isAdmin && forUserId && forUserId !== "SELF" && forUserId !== currentUserId) {
        payload.user_id = forUserId;
      }
      await api.post("/vacations", payload);
      toast.success(isAdmin && forUserId !== "SELF" ? "Urlaub für Mitarbeiter eingetragen" : "Antrag eingereicht");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const onBehalfOfOther = isAdmin && forUserId !== "SELF" && forUserId !== currentUserId;

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="font-display">
          {onBehalfOfOther ? "Urlaub für Mitarbeiter eintragen" : "Neuer Urlaubsantrag"}
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        {isAdmin && employees && employees.length > 0 && (
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-semibold text-slate-600">Für Mitarbeiter</Label>
            <Select value={forUserId} onValueChange={setForUserId}>
              <SelectTrigger data-testid="vac-for-user"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="SELF">Mich selbst (als Antrag)</SelectItem>
                {employees.filter((e) => e.id !== currentUserId).map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {onBehalfOfOther && (
              <p className="text-[11px] text-emerald-700">
                Wird automatisch als „genehmigt" gespeichert und vom Resturlaub abgezogen.
              </p>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-semibold text-slate-600">Von</Label>
            <Input data-testid="vac-start" type="date" required value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-semibold text-slate-600">Bis</Label>
            <Input data-testid="vac-end" type="date" required value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase font-semibold text-slate-600">Grund (optional)</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="z.B. Familienurlaub" />
        </div>
        {conflict && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800 flex gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              Überschneidung mit genehmigtem Urlaub von <strong>{conflict.user_name}</strong> ({conflict.start_date} – {conflict.end_date}).
              {" "}{onBehalfOfOther ? "Bitte zuerst stornieren oder anderen Zeitraum wählen." : "Dein Antrag wird abgelehnt werden müssen, falls du derselbe Mitarbeiter bist – sonst ist eine Genehmigung blockiert."}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button type="submit" data-testid="vac-submit" disabled={busy} className="bg-kauz-navy hover:bg-kauz-navy-dark">
            {busy ? "Senden…" : "Antrag stellen"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function CompanyVacationsPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "", description: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/company-vacations");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/company-vacations", form);
      toast.success("Betriebsferien angelegt – wurde allen Mitarbeitern abgezogen");
      setForm({ name: "", start_date: "", end_date: "", description: "" });
      setOpen(false);
      await load();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Betriebsferien „${name}" wirklich löschen? Die Tage werden allen Mitarbeitern wieder gutgeschrieben.`)) return;
    try {
      await api.delete(`/company-vacations/${id}`);
      toast.success("Gelöscht");
      await load();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="space-y-4">
      <div className="ph-card p-4 md:p-5">
        <div className="flex items-start justify-between gap-3 flex-col md:flex-row">
          <div>
            <h3 className="font-display text-base font-semibold flex items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-600" /> Betriebsferien verwalten
            </h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              Lege Zeiträume fest, in denen der Betrieb komplett geschlossen ist. Diese Werktage werden allen aktiven Mitarbeitern automatisch von ihrem Resturlaub abgezogen. Persönliche Urlaubsanträge in diesem Zeitraum werden blockiert.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-company-vac" className="bg-kauz-navy hover:bg-kauz-navy-dark h-10">
                <Plus className="h-4 w-4 mr-2" /> Neue Betriebsferien
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-display">Betriebsferien anlegen</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase font-semibold text-slate-600">Bezeichnung</Label>
                  <Input data-testid="cv-name" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="z.B. Weihnachtsferien 2026" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase font-semibold text-slate-600">Von</Label>
                    <Input data-testid="cv-start" type="date" required value={form.start_date}
                      onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase font-semibold text-slate-600">Bis</Label>
                    <Input data-testid="cv-end" type="date" required value={form.end_date}
                      onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase font-semibold text-slate-600">Notiz (optional)</Label>
                  <Textarea value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 flex gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  Diese Werktage werden allen aktiven Mitarbeitern direkt vom Resturlaub abgezogen.
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="cv-submit" disabled={busy} className="bg-kauz-navy hover:bg-kauz-navy-dark">
                    {busy ? "Speichern…" : "Anlegen"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="ph-card p-12 text-center text-slate-400 text-sm">
          <Building2 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          Noch keine Betriebsferien festgelegt.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((c) => (
            <li key={c.id} data-testid={`company-row-${c.id}`} className="ph-card p-4 flex items-center gap-4">
              <div className="h-10 w-10 rounded-md bg-slate-100 border border-slate-300 grid place-items-center">
                <Building2 className="h-5 w-5 text-slate-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                <div className="text-xs text-slate-500">
                  {c.start_date} → {c.end_date} · {c.days} Werktage · angelegt von {c.created_by_name}
                </div>
                {c.description && <div className="text-xs text-slate-500 mt-1 italic">„{c.description}"</div>}
              </div>
              <Button variant="outline" size="sm" className="h-8 text-red-700 border-red-200 hover:bg-red-50"
                onClick={() => del(c.id, c.name)} data-testid={`cv-delete-${c.id}`}>
                <Trash2 className="h-4 w-4 mr-1" /> Löschen
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

